/**
 * Settings primitives from mockup 5: `.card` with its `.ch` header, `.srow` rows,
 * `.tog` toggle, and the `.card.danger` variant.
 *
 * Two things this buys that the old panels could not express.
 *
 * A `.srow` puts the label, its help text, and its control on one row, so a
 * setting's explanation sits with the thing it explains. The panels previously
 * used four different densities and put help text wherever it fitted.
 *
 * `.card.danger` gives destructive settings a visibly different container. Every
 * destructive action in this app already has a confirmation, but a confirmation
 * only fires once you have decided to click. Making the container read as
 * dangerous is what stops the click.
 */

export function SettingsCard({ title, description, danger = false, action, children }) {
  return (
    <div
      className={`rounded-card border shadow-e1 overflow-hidden
        ${danger
          ? 'border-red-300/70 dark:border-red-900/70'
          : 'border-line'} bg-surface`}
    >
      <div
        className={`flex items-center gap-2.5 px-3.5 py-3 border-b
          ${danger
            ? 'bg-red-50/70 dark:bg-red-950/25 border-red-200 dark:border-red-900/60'
            : 'bg-surface-2 border-line'}`}
      >
        <div className="min-w-0 flex-1">
          {/* Same brand ink as PageHeader so card titles match page titles. */}
          <p className={`text-[13px] font-bold tracking-[-0.005em] ${danger ? 'text-red-700 dark:text-red-400' : 'text-[#3B5452] dark:text-white'}`}>
            {title}
          </p>
          {description && (
            <p className="text-[11.5px] text-ink-3 mt-0.5 leading-[1.45]">{description}</p>
          )}
        </div>
        {action}
      </div>
      <div>{children}</div>
    </div>
  );
}

/** One setting: label and help on the left, control on the right. */
export function SettingRow({ label, help, badge, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 sm:gap-4 items-start sm:items-center
                    px-3.5 py-3 border-b border-line last:border-b-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap text-[12.5px] font-semibold text-ink">
          {label}
          {badge}
        </div>
        {help && <p className="text-[11.5px] text-ink-3 leading-[1.5] mt-[3px] max-w-[62ch]">{help}</p>}
      </div>
      <div className="flex items-center gap-2 flex-none">{children}</div>
    </div>
  );
}

/** `.tog` — 36×20 track, 14px knob. */
export function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full border transition-colors flex-none
        ${checked ? 'bg-accent border-accent' : 'bg-surface-2 border-line-strong'}`}
    >
      <span
        className="absolute top-[2px] w-3.5 h-3.5 rounded-full bg-surface shadow-e1 transition-[left]"
        style={{ left: checked ? 18 : 2 }}
      />
    </button>
  );
}

/** `.radio` — used where a setting is one-of-N rather than on/off. */
export function RadioRow({ options, value, onChange, name }) {
  return (
    <div className="flex flex-col gap-1.5">
      {options.map((option) => {
        const on = value === option.value;
        return (
          <label key={option.value} className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={on}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            <span
              className={`w-[15px] h-[15px] rounded-full border-[1.5px] flex-none inline-flex items-center justify-center
                bg-surface ${on ? 'border-accent' : 'border-line-strong'}`}
            >
              {on && <span className="w-[7px] h-[7px] rounded-full bg-accent" />}
            </span>
            <span className="text-[12.5px] text-ink-2">{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}
